import type { PresentationSlide } from './presentationHooks';

const UNSET_SITSTAT = 'unset';

export const FEMA_OPS_PRESENTATION_SOURCE = 'fema-ops';
export const FEMA_OPS_PRESENTATION_NAME = 'Daily Operations Briefing';
export const FEMA_UNEDITED_SEED_SLIDE_ID = 'fema-cover';

/**
 * Manual OPORD Mission template, reused so the national report can name its own
 * sections.
 */
export const FEMA_MANUAL_SECTION_TEMPLATE_ID =
  'b5c90002-0000-0000-0000-000000000002';

export const OPORD_GENERAL_SECTION_TEMPLATE_ID =
  'b5c90016-0000-0000-0000-000000000016';

export type FemaRegionRoman = 'II' | 'IV' | 'IX';

export type FemaOpsBriefingSources = {
  sitstatTemplateId?: string | null;
  nationalReportId: string;
  regionReportIds: Record<FemaRegionRoman, string>;
};

function slide(
  id: string,
  fields: Omit<PresentationSlide, 'id' | 'notes'>
): PresentationSlide {
  return { id, notes: '', ...fields };
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function bulletsHtml(lines: string[]): string {
  return `<ul>${lines.map((line) => `<li>${escapeHtml(line)}</li>`).join('')}</ul>`;
}

function paragraphsHtml(lines: string[]): string {
  return lines.map((line) => `<p>${escapeHtml(line)}</p>`).join('');
}

const MONITORING_SECTIONS: { name: string; bullets: string[] }[] = [
  {
    name: 'New Significant Incidents / Ongoing Operations',
    bullets: [
      'Hurricane Nolo (CAT-4) – Central Pacific – FINAL',
      "Nor'easter – Mid-Atlantic to Northeast – FINAL",
    ],
  },
  {
    name: 'Hazard Monitoring',
    bullets: [
      'Excessive Rainfall / Flash Flooding – Four Corners Region, Northern Plains',
      'Atlantic: Tropical Depression Fay',
      'Atlantic: Disturbance 1 (AL91): High (80%)',
      'Eastern and Central Pacific: Hurricane Nolo (CAT-4)',
      'Eastern and Central Pacific: Hurricane Polo (CAT-3)',
      'Eastern and Central Pacific: Tropical Storm Rachel',
      'Eastern and Central Pacific: Disturbance 1: High (90%)',
    ],
  },
  {
    name: 'Disaster Declaration Activity',
    bullets: ['No new declaration activity'],
  },
  {
    name: 'Event Monitoring',
    bullets: ['No new significant events'],
  },
];

export const FEMA_NATIONAL_SECTIONS: { name: string; html: string }[] = [
  {
    name: 'Daily Operations Briefing',
    html: paragraphsHtml([
      'FEMA National Watch Center',
      'Daily Operations Briefing',
      'Monday, September 28, 2026',
      '8:30 a.m. ET',
    ]),
  },
  ...MONITORING_SECTIONS.map((section) => ({
    name: section.name,
    html: bulletsHtml(section.bullets),
  })),
];

const STORM_SECTIONS: { title: string; subtitle: string; bullets: string[] }[] =
  [
    {
      title: 'Tropical Storm Rachel – Eastern Pacific',
      subtitle: 'Situation (Advisory #5A, as of 8:00 a.m. ET)',
      bullets: [
        'Located 250 miles SSW of Acapulco, Mexico',
        'Moving WNW at 8 mph',
        'Maximum sustained winds 45 mph',
        'Tropical-storm-force winds extend outward up to 140 miles from the center',
        'Gradual strengthening is expected during the next few days, and Rachel is forecast to become a hurricane by mid-week',
      ],
    },
    {
      title: 'Hurricane Polo (CAT-3) – Eastern Pacific',
      subtitle: 'Situation (Advisory #31A, as of 8:00 a.m. ET)',
      bullets: [
        'Located 110 miles SW of Cabo San Lazaro, Mexico',
        'Moving NNE at 10 mph',
        'Maximum sustained winds of 115 mph',
        'Hurricane-force winds extend up to 40 miles from center; tropical-storm-force winds extend up to 105 miles from center',
        'Polo is forecast to be near major hurricane strength when it reaches Baja California Sur later today, and is anticipated to be a hurricane when it makes landfall in mainland Mexico late tonight or early Tuesday morning',
      ],
    },
    {
      title: 'Hurricane Nolo (CAT-4) – Central Pacific',
      subtitle: 'Situation (Advisory #31A, as of 8:00 a.m. ET)',
      bullets: [
        'Located 405 miles SW of Honolulu, Hawaii',
        'Maximum sustained winds of 155 mph',
        'Moving WNW at 12 mph',
        'Hurricane-force winds extend up to 35 miles from center; tropical-storm-force winds extend up to 150 miles from center',
        'Some additional strengthening is expected, and Nolo could become a category 5 hurricane later today',
        'A gradual weakening trend is likely to begin tonight',
      ],
    },
  ];

export function femaOpordName(roman: FemaRegionRoman): string {
  return `OPORD: FEMA Region ${roman}`;
}

export const FEMA_REGION_OPORD_HTML: Record<FemaRegionRoman, string> = {
  II: [
    paragraphsHtml([
      "Nor'easter – Mid-Atlantic to Northeast",
      "The nor'easter is final. Region II remains on watch for residual coastal and inland impacts across the Northeast.",
    ]),
    bulletsHtml(["Nor'easter – Mid-Atlantic to Northeast – FINAL"]),
  ].join(''),
  IV: [
    paragraphsHtml([
      'Southeast Region IV is on watch for national operations and declaration activity.',
    ]),
    bulletsHtml(['No new declaration activity', 'No new significant events']),
  ].join(''),
  IX: STORM_SECTIONS.map(
    (storm) =>
      `<p><strong>${escapeHtml(storm.title)}</strong></p><p>${escapeHtml(storm.subtitle)}</p>${bulletsHtml(storm.bullets)}`
  ).join(''),
};

/**
 * FEMA National Watch Center daily operations briefing. Slides 1–2 are widgets
 * on the national report. Slides 3–5 are regional OPORDs. The last slide is the
 * Hub sitstat.
 */
export function buildFemaOpsBriefing(
  sources: FemaOpsBriefingSources
): PresentationSlide[] {
  return [
    slide('fema-overview', {
      kind: 'report',
      sourceId: sources.nationalReportId,
      layout: 'widgets',
      widgetCount: 1,
      widgetOffset: 0,
    }),
    slide('fema-monitoring', {
      kind: 'report',
      sourceId: sources.nationalReportId,
      layout: 'widgets',
      widgetCount: 4,
      widgetOffset: 1,
    }),
    slide('fema-region-ii', {
      kind: 'report',
      sourceId: sources.regionReportIds.II,
      layout: 'document',
    }),
    slide('fema-region-iv', {
      kind: 'report',
      sourceId: sources.regionReportIds.IV,
      layout: 'document',
    }),
    slide('fema-region-ix', {
      kind: 'report',
      sourceId: sources.regionReportIds.IX,
      layout: 'document',
    }),
    slide('fema-cop', {
      kind: 'sitstat',
      sourceId: sources.sitstatTemplateId || UNSET_SITSTAT,
    }),
  ];
}

export function isUneditedFemaSeed(
  slides: Pick<PresentationSlide, 'id'>[]
): boolean {
  return slides[0]?.id === FEMA_UNEDITED_SEED_SLIDE_ID;
}

export function isUnsetSitstat(sourceId: string): boolean {
  return sourceId === UNSET_SITSTAT;
}
